import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { killBodyScroll, initBodyScroll } from '../../common/overlayScrollbars.js'
import { OverlayScrollbars } from "overlayscrollbars"
import VidstackPlayer from './VidstackPlayer.jsx'
import Transcript from './Transcript.jsx'
import RelatedVideosCarousel from './RelatedVideosCarousel.jsx'
import PlayNextOverlay from './PlayNextOverlay.jsx'
import VideoProgressManager from './videoProgress.js'
// Tippy removed - using native title attributes for React 19 compatibility
import 'sharer.js';

const videoProgressManager = new VideoProgressManager();

const VideoModal = ({ isOpen, onClose, videoData, fullscreen = false, freshStart = false, autoplay = true }) => {

    const [isLoading, setIsLoading] = useState(false)
    const [isFullScreen, setIsFullScreen] = useState(false)
    const [isTranscriptOpen, setIsTranscriptOpen] = useState(false)
    const [transcriptError, setTranscriptError] = useState(null)
    const [wasInFullscreen, setWasInFullscreen] = useState(false)
    const [subtitleData, setSubtitleData] = useState(null)
    const [isMobile, setIsMobile] = useState(false)
    const [currentTime, setCurrentTime] = useState(0)
    const [showPlayNext, setShowPlayNext] = useState(false)
    const modalRef = useRef(null)
    const scrollbarRef = useRef(null)
    const playerRef = useRef(null)
    const playerContainerRef = useRef(null)
    const wasInFullscreenRef = useRef(false)

    const currentTimeRef = useRef(0)

    if (!isOpen) {
        return null;
    }

    const handleClose = useCallback(() => {
        onClose()
    }, [onClose])

    const handleOverlayClick = useCallback((e) => {
        const target = e.target;
        const isOverlay = target.classList.contains('video-modal');
        const isScrollContainer = target.classList.contains('video-modal__scroll');
        const isOverlayScrollbarsViewport = target.hasAttribute('data-overlayscrollbars-viewport');

        if ((isOverlay || isScrollContainer || isOverlayScrollbarsViewport) && !target.closest('.video-modal__container')) {
            handleClose();
        }
    }, [handleClose]);

    useEffect(() => {
        if (isOpen && fullscreen && playerRef.current) {
            const timeoutId = setTimeout(() => {
                try {
                    // Enter fullscreen on the modal's own player iframe
                    if (typeof playerRef.current.toggleFullscreen === 'function') {
                        playerRef.current.toggleFullscreen()
                    }
                    if (playerRef.current.unmute) {
                        playerRef.current.unmute()
                    }
                } catch (error) {
                    console.warn('Error entering fullscreen:', error)
                }
            }, 1000)

            return () => clearTimeout(timeoutId)
        }
    }, [isOpen, fullscreen])

    useEffect(() => {
        if (isOpen && scrollbarRef.current) {
            const timeoutId = setTimeout(() => {
                const viewport = modalRef.current?.querySelector('[data-overlayscrollbars-viewport]')
                if (viewport) {
                    viewport.addEventListener('click', handleOverlayClick)
                }
            }, 100)

            return () => {
                clearTimeout(timeoutId)
                const viewport = modalRef.current?.querySelector('[data-overlayscrollbars-viewport]')
                if (viewport) {
                    viewport.removeEventListener('click', handleOverlayClick)
                }
            }
        }
    }, [isOpen, handleOverlayClick])

    useEffect(() => {
        if (isOpen) {
            killBodyScroll()

            if (modalRef.current) {
                const scrollContainer = modalRef.current.querySelector('.video-modal__scroll')
                if (scrollContainer) {
                    scrollbarRef.current = OverlayScrollbars(scrollContainer, {
                        scrollbars: {
                            autoHide: 'never',
                        },
                        overflow: {
                            x: 'hidden',
                            y: 'scroll',
                        },
                    })
                }
            }
        } else {
            if (scrollbarRef.current) {
                scrollbarRef.current.destroy()
                scrollbarRef.current = null
            }

            initBodyScroll()
        }
    }, [isOpen])

    useEffect(() => {
        if (!isOpen || !isTranscriptOpen) return

        const adjustTranscriptHeight = () => {
            const playerElement = modalRef.current?.querySelector('.video-modal__player')
            const transcriptElement = modalRef.current?.querySelector('.video-modal__transcript')

            if (playerElement && transcriptElement) {
                const playerHeight = playerElement.offsetHeight
                transcriptElement.style.height = `${playerHeight}px`
            }
        }

        adjustTranscriptHeight()

        const handleResize = () => {
            adjustTranscriptHeight()
        }

        window.addEventListener('resize', handleResize)

        const timeoutId = setTimeout(adjustTranscriptHeight, 100)

        return () => {
            window.removeEventListener('resize', handleResize)
            clearTimeout(timeoutId)
        }
    }, [isOpen, isTranscriptOpen])

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 1024)
        }

        checkMobile()
        window.addEventListener('resize', checkMobile)

        return () => window.removeEventListener('resize', checkMobile)
    }, [])

    useEffect(() => {
        if (videoData?.transcript_vtt?.url) {
            setSubtitleData(videoData.transcript_vtt.url)
        } else {
            setSubtitleData(null)
        }
    }, [videoData])

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose()
            }
        }

        document.addEventListener('keydown', handleEscape)
        return () => document.removeEventListener('keydown', handleEscape)
    }, [isOpen, onClose])

    const handlePlayClick = useCallback(async (e) => {
        e.preventDefault()
        e.stopPropagation()

        if (playerRef && playerRef.current) {
            try {
                playerRef.current.unmute()

                const currentTime = playerRef.current.currentTime || 0;
                const duration = playerRef.current.duration || 0;
                const hasEnded = currentTime >= duration && duration > 0;

                if (hasEnded) {
                    playerRef.current.play()
                    return
                }
            } catch (error) {
                console.warn('Error unmuting video:', error)
            }
        }

        try {
            if (playerRef && playerRef.current && typeof playerRef.current.toggleFullscreen === 'function') {
                playerRef.current.toggleFullscreen(e)
            } else {
                console.warn('Player ref or toggleFullscreen not available')
            }
        } catch (error) {
            console.warn('Error toggling fullscreen:', error)
        }
    }, [playerRef])

    const handlePauseClick = useCallback(async (e) => {
        e.preventDefault()
        e.stopPropagation()

        try {
            if (playerRef && playerRef.current && typeof playerRef.current.pause === 'function') {
                playerRef.current.pause()
            } else {
                console.warn('Player ref or pause method not available')
            }
        } catch (error) {
            console.warn('Error pausing video:', error)
        }
    }, [playerRef])

    const handleToggleTranscript = useCallback(() => {
        setIsTranscriptOpen(!isTranscriptOpen)
    }, [isTranscriptOpen])

    const handleTimeUpdate = useCallback((time) => {
        setCurrentTime(time)
    }, [])

    const handleSeek = useCallback((time) => {
        try {
            if (playerRef.current && typeof playerRef.current.seek === 'function') {
                playerRef.current.seek(time)
            } else if (playerRef.current) {
                playerRef.current.currentTime = time
            }
        } catch (error) {
            console.warn('Error seeking video:', error)
        }
    }, [])

    const handleTranscriptClose = useCallback(() => {
        setIsTranscriptOpen(false)
        setTranscriptError(null)
    }, [])

    const handleTranscriptError = useCallback((error) => {
        console.warn('Transcript error:', error)
        setTranscriptError(error.message)
    }, [])

    const handleFullscreenChange = useCallback((isFullscreen, nativeEvent) => {
        setIsFullScreen(isFullscreen)

        if (isFullscreen) {
            setWasInFullscreen(true)
            wasInFullscreenRef.current = true
        }
    }, [])

    const handleFullscreenError = useCallback((error, nativeEvent) => {
        console.warn('Fullscreen error:', error)
    }, [])

    // Filter out "coming soon" videos from next-up
    const nextVideo = videoData?.related_videos?.find(v => !v.is_coming_soon) || null
    const notifyConfig = videoData?.notifyConfig || null

    // Resolve heading/subheading: carousel override > Page Settings default (passed via notifyConfig)
    const notifyHeading = notifyConfig?.heading || eyfs.notify_heading || ''
    const notifySubheading = notifyConfig?.subheading || eyfs.notify_subheading || ''

    // Track if the play-next overlay should be shown in fullscreen-style (viewport-covering)
    const [playNextFullscreen, setPlayNextFullscreen] = useState(false)
    const [showNotifyOverlay, setShowNotifyOverlay] = useState(false)

    const handleVideoEnded = useCallback(() => {
        const isCurrentlyFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement)

        // Exit iframe fullscreen if active
        if (isCurrentlyFullscreen) {
            ;(document.exitFullscreen || document.webkitExitFullscreen)?.call(document)
        }

        if (!nextVideo) {
            // No playable next video — always show notification overlay
            setShowNotifyOverlay(true)
            return
        }

        // Show overlay — in fullscreen-style mode if user was watching fullscreen
        // Use ref to avoid re-creating this callback when wasInFullscreen changes
        // (that would cause the YouTube player to be destroyed/recreated)
        setPlayNextFullscreen(isCurrentlyFullscreen || wasInFullscreenRef.current)
        setShowPlayNext(true)
    }, [nextVideo, notifyConfig])

    const handleVideoPlaying = useCallback(() => {
        // User replayed via YouTube controls — dismiss overlay
        setShowPlayNext(false)
    }, [])

    const handlePlayNext = useCallback((isUserClick) => {
        if (!nextVideo) return

        const shouldGoFullscreen = playNextFullscreen && isUserClick

        setShowPlayNext(false)
        setPlayNextFullscreen(false)

        // Simulate clicking a TV card link SYNCHRONOUSLY (preserves user gesture)
        // VideoModalManager's document click handler intercepts <a href="/fstv/{slug}/">
        // and handles everything: fullscreen iframe, AJAX, modal open
        window.__playNextFresh = true
        const link = document.createElement('a')
        link.href = `/fstv/${nextVideo.slug}/`
        if (shouldGoFullscreen) link.dataset.fullscreen = 'true'
        link.style.display = 'none'
        document.body.appendChild(link)

        // Close current modal — sets isOpen=false so new openVideoModal can proceed
        onClose()

        // Click the link — MUST be synchronous for user gesture (fullscreen)
        // VideoModalManager's click handler will pick this up
        link.click()
        link.remove()
    }, [nextVideo, onClose, playNextFullscreen])

    const handleSeeAllEpisodes = useCallback(() => {
        setShowPlayNext(false)

        // Exit fullscreen first if active (need to scroll to related section)
        if (document.fullscreenElement || document.webkitFullscreenElement) {
            ;(document.exitFullscreen || document.webkitExitFullscreen)?.call(document)
        }

        // Small delay to let fullscreen exit complete before scrolling
        setTimeout(() => {
            const relatedSection = modalRef.current?.querySelector('.video-modal__related')
            if (relatedSection) {
                relatedSection.scrollIntoView({ behavior: 'smooth' })
            }
        }, 300)
    }, [])

    if (!isOpen || !videoData) return null

    useEffect(() => {
      if (window.Sharer) {
        window.Sharer.init();
      }
    }, []);

    useEffect(() => {
        if (isOpen && videoData?.related_videos && videoData.related_videos.length > 0) {
            setTimeout(() => {
                videoProgressManager.updateProgressBars();
            }, 300);
        }
    }, [isOpen, videoData?.related_videos]);

    const descriptionContent = useMemo(() => {
        if (!videoData.description) return null;

        return (
            <div className="video-modal__content--description">
                <div
                    dangerouslySetInnerHTML={{ __html: videoData.description }}
                />

                {videoData.transcript && !isMobile && (
                    <button
                        className="show-transcript"
                        onClick={handleToggleTranscript}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="42" height="42" fill="none"><path d="M16 11h-1c-1.4 0-2.101 0-2.636.273a2.5 2.5 0 0 0-1.092 1.092C11 12.9 11 13.6 11 15v12c0 1.4 0 2.1.273 2.635a2.5 2.5 0 0 0 1.092 1.093c.534.272 1.234.272 2.631.272H16m0-20h11c1.4 0 2.099 0 2.634.273a2.5 2.5 0 0 1 1.094 1.092c.272.534.272 1.234.272 2.631v12.008c0 1.397 0 2.096-.272 2.63-.24.47-.623.853-1.094 1.093-.534.272-1.233.272-2.63.272H16m0-20v20m5-11.25h5M21 16h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        {isTranscriptOpen ? 'Hide Transcript' : 'Show Transcript'}
                    </button>
                )}
            </div>
        );
    }, [videoData.description, videoData.transcript, isMobile, isTranscriptOpen, handleToggleTranscript]);

    return (
        <div
            ref={modalRef}
            className={`video-modal is-active ${isTranscriptOpen ? 'has-transcript-open' : ''}`}
            onClick={handleOverlayClick}
        >
            <button
                className="video-modal__close"
                aria-label="Close Video"
                onClick={handleClose}
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M20.736 19.463A.9.9 0 0 1 21 20.1a.9.9 0 0 1-.264.637.9.9 0 0 1-.637.264.9.9 0 0 1-.637-.264L12 13.272l-7.463 7.464a.9.9 0 0 1-1.273 0 .9.9 0 0 1 0-1.273L10.728 12 3.264 4.537a.9.9 0 0 1 0-1.273.9.9 0 0 1 1.273 0L12 10.728l7.463-7.464a.9.9 0 0 1 1.273 0 .9.9 0 0 1 0 1.273L13.272 12l7.464 7.463z" fill="#fff" /></svg>
            </button>

            <div className="video-modal__scroll" onClick={handleOverlayClick}>
                <div className="video-modal__container">
                    <div className="video-modal__player" ref={playerContainerRef}>
                        <div className="video-modal__player--video">
                            {videoData.video_source === 'vimeo' && videoData.vimeo_id ? (
                                <iframe
                                    className="video-modal__vimeo-iframe"
                                    src={`https://player.vimeo.com/video/${videoData.vimeo_id}?${autoplay ? 'autoplay=1&' : ''}title=0&byline=0&portrait=0`}
                                    allow="autoplay; fullscreen; picture-in-picture"
                                    allowFullScreen
                                    title={videoData.title || 'Vimeo video'}
                                    style={{ width: '100%', height: '100%', border: 0 }}
                                />
                            ) : (
                                <VidstackPlayer
                                    ref={playerRef}
                                    key={`${videoData.video_source || 'youtube'}-${videoData.youtube_id || videoData.video_file_url || ''}`}
                                    source={videoData.video_source || 'youtube'}
                                    videoId={videoData.youtube_id}
                                    fileUrl={videoData.video_file_url || ''}
                                    featuredImageUrl={videoData.featured_image_url || ''}
                                    onLoadingChange={setIsLoading}
                                    onTimeUpdate={handleTimeUpdate}
                                    onFullscreenChange={handleFullscreenChange}
                                    onFullscreenError={handleFullscreenError}
                                    onEnded={handleVideoEnded}
                                    onPlaying={handleVideoPlaying}
                                    isMobile={isMobile}
                                    autoplay={autoplay}
                                    skipResume={freshStart}
                                    subtitleUrl={subtitleData}
                                    title={videoData.title || ''}
                                />
                            )}
                        </div>

                        {showPlayNext && nextVideo && !playNextFullscreen && (
                            <PlayNextOverlay
                                nextVideo={nextVideo}
                                onPlayNext={handlePlayNext}
                                onSeeAllEpisodes={handleSeeAllEpisodes}
                            />
                        )}

                        {showNotifyOverlay && (eyfs.enable_email_notifications || eyfs.enable_push_notifications) && (
                            <div className="video-modal__notify-overlay">
                                {notifyHeading && (
                                    <h4>{notifyHeading}</h4>
                                )}
                                {notifySubheading && (
                                    <h5>{notifySubheading}</h5>
                                )}
                                <div className="notify-box" data-campaign={videoData.channel_name || 'EY FS TV'}>
                                    <div className="notify-box__buttons">
                                        {eyfs.enable_email_notifications && (
                                        <button className="notify-box__btn notify-box__btn--email" type="button">
                                            <img src={`${eyfs.assets_url}/images/Notification-Email.svg`} alt="" width="28" height="28" />
                                            <span>Sign up for Email Alerts</span>
                                        </button>
                                        )}
                                        {eyfs.enable_push_notifications && (
                                        <button className="notify-box__btn notify-box__btn--push" type="button">
                                            <img src={`${eyfs.assets_url}/images/Notification-Device.svg`} alt="" width="28" height="28" />
                                            <span>Receive Push Alerts</span>
                                        </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {videoData.transcript && isTranscriptOpen && !isMobile && (
                        <div className="video-modal__transcript">
                            <div className="video-modal__transcript--header">
                                <div className="video-modal__transcript--heading">
                                    Transcription
                                </div>
                                <button
                                    className="video-modal__transcript--close"
                                    aria-label="Close Transcription"
                                    onClick={handleTranscriptClose}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            d="M18 6L6 18M6 6l12 12"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </button>
                            </div>

                            {transcriptError ? (
                                <div className="video-modal__transcript--error">
                                    {transcriptError}
                                </div>
                            ) : (
                                <Transcript
                                    transcriptUrl={videoData.transcript.url}
                                    currentTime={currentTime}
                                    onSeek={handleSeek}
                                    onError={handleTranscriptError}
                                />
                            )}
                        </div>
                    )}

                    {(videoData.title || videoData.description || (videoData.date && videoData.date.trim()) || (videoData.topics && videoData.topics.length > 0) || (videoData.speakers && videoData.speakers.length > 0)) && (
                    <div className="video-modal__content">
                        <div className="video-modal__content--meta">
                            {/*{videoData.topics && videoData.topics.length > 0 && (*/}
                            {/*    <div className="tag outline">*/}
                            {/*        {videoData.topics[0].name}*/}
                            {/*    </div>*/}
                            {/*)}*/}

                            {videoData.date && videoData.date.trim() && (
                            <div className="date">
                                {videoData.date}
                            </div>
                            )}
                        </div>

                        {descriptionContent}

                        <div className="video-modal__content--speakers">
                              {videoData.speakers && videoData.speakers.length > 0 && (
                                <>
                                <div className="title">Speakers</div>

                                <ul>
                                    {videoData.speakers.map((speaker, index) => (
                                        <li key={index}>
                                            <a href={speaker.url}>{speaker.name}</a>
                                        </li>
                                    ))}
                                </ul>
                                </>
                              )}
                              <div className="video-modal__content--share">
                                <button
                                  className="twitter jsSocialShare"
                                  data-sharer="twitter"
                                  title="X"
                                  data-title={videoData?.title || ''}
                                  data-url={window.location.href}>
                                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" xmlns:v="https://vecta.io/nano"><path d="M17.542 15.658L21.634 11h-.969l-3.555 4.044L14.273 11H11l4.291 6.116L11 22h.969l3.752-4.271L18.718 22h3.273M12.32 11.716h1.489l6.856 9.603h-1.49M27.817 16A11.82 11.82 0 0 0 16 4.183 11.82 11.82 0 0 0 4.183 16 11.82 11.82 0 0 0 16 27.817 11.82 11.82 0 0 0 27.817 16zM29 16c0 7.18-5.82 13-13 13S3 23.18 3 16 8.82 3 16 3s13 5.82 13 13z"/></svg>
                                </button>
                                <button
                                  className="linkedin jsSocialShare"
                                  title="Linkedin"
                                  data-sharer="linkedin"
                                  data-title={videoData?.title || ''}
                                  data-url={window.location.href}>
                                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" xmlns:v="https://vecta.io/nano"><path d="M13.64 20.604c0 .22-.176.352-.352.352h-1.584c-.22 0-.352-.176-.352-.352v-6.6c0-.22.176-.352.352-.352h1.584c.22 0 .352.176.352.352v6.6zm-1.144-7.612c-.836 0-1.496-.66-1.496-1.496S11.66 10 12.496 10s1.496.66 1.496 1.496-.704 1.496-1.496 1.496zm9.372 7.656c0 .176-.132.352-.352.352h-1.672c-.176 0-.352-.132-.352-.352v-3.124c0-.484.132-2.024-1.188-2.024-1.056 0-1.276 1.056-1.276 1.54v3.564c0 .176-.132.352-.352.352h-1.628c-.176 0-.352-.132-.352-.352V13.96c0-.176.132-.352.352-.352h1.628c.176 0 .352.132.352.352v.572c.396-.572.968-1.012 2.2-1.012 2.728 0 2.684 2.552 2.684 3.916l-.044 3.212zM27.817 16A11.82 11.82 0 0 0 16 4.183 11.82 11.82 0 0 0 4.183 16 11.82 11.82 0 0 0 16 27.817 11.82 11.82 0 0 0 27.817 16zM29 16c0 7.18-5.82 13-13 13S3 23.18 3 16 8.82 3 16 3s13 5.82 13 13z"/></svg>
                                </button>
                                <button
                                  className="share jsShareLink"
                                  title="Copy link"
                                  data-link={window.location.href}>
                                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" xmlns:v="https://vecta.io/nano"><path d="M11.867 15.725c.165-.165.433-.165.598 0s.165.433 0 .598l-.954.953c-.887.887-.887 2.326 0 3.213s2.326.887 3.214 0l.952-.953c.165-.165.433-.166.598-.001s.165.434 0 .599l-.952.952a3.12 3.12 0 0 1-4.41.001 3.12 3.12 0 0 1 0-4.409l.954-.953zm5.24-1.429c.165-.165.433-.165.598 0s.165.433 0 .598l-3.811 3.811c-.165.165-.433.165-.598 0s-.165-.433 0-.598l3.811-3.811zm-1.429-2.382a3.12 3.12 0 0 1 4.409 0 3.12 3.12 0 0 1 0 4.41l-.001-.001-.952.953c-.165.165-.433.166-.598.001s-.165-.433 0-.598l.953-.953c.887-.887.887-2.326 0-3.213a2.27 2.27 0 0 0-3.213 0l-.954.953c-.165.165-.433.165-.598 0s-.165-.433 0-.598l.954-.953zM27.817 16A11.82 11.82 0 0 0 16 4.183 11.82 11.82 0 0 0 4.183 16 11.82 11.82 0 0 0 16 27.817 11.82 11.82 0 0 0 27.817 16zM29 16c0 7.18-5.82 13-13 13S3 23.18 3 16 8.82 3 16 3s13 5.82 13 13z"/></svg>
                                </button>
                              </div>
                              {(eyfs.enable_email_notifications || eyfs.enable_push_notifications) && (
                              <div className="notify-box" data-campaign={videoData.channel_name || 'EY FS TV'}>
                                <div className="notify-box__buttons">
                                    {eyfs.enable_email_notifications && (
                                    <button className="notify-box__btn notify-box__btn--email" type="button">
                                        <img src={`${eyfs.assets_url}/images/Notification-Email.svg`} alt="" width="40" height="40" />
                                        <span>Sign up for Email Alerts</span>
                                    </button>
                                    )}
                                    {eyfs.enable_push_notifications && (
                                    <button className="notify-box__btn notify-box__btn--push" type="button">
                                        <img src={`${eyfs.assets_url}/images/Notification-Device.svg`} alt="" width="40" height="40" />
                                        <span>Receive Push Alerts</span>
                                    </button>
                                    )}
                                </div>
                              </div>
                              )}
                            </div>
                    </div>
                    )}
                    {videoData.related_videos && videoData.related_videos.length > 0 && (
                        <div className="video-modal__related g4">
                            <RelatedVideosCarousel
                                videos={videoData.related_videos}
                                title={videoData.channel_name ? `More from ${videoData.channel_name}` : 'More Videos'}
                                onVideoSelect={(slug) => {
                                    onClose();
                                    // Use a simple timeout to ensure modal closes before opening new one
                                    setTimeout(() => {
                                        history.pushState({}, '', `/fstv/${slug}/`);
                                        window.dispatchEvent(new PopStateEvent('popstate'));
                                    }, 100);
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>

            {showPlayNext && nextVideo && playNextFullscreen && createPortal(
                <PlayNextOverlay
                    nextVideo={nextVideo}
                    onPlayNext={handlePlayNext}
                    onSeeAllEpisodes={handleSeeAllEpisodes}
                    isFullscreen={true}
                />,
                document.body
            )}
        </div>
    )
}

export default VideoModal
